import { useCallback, useState } from "react";
import { AccordionDetails } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { Delete } from "@mui/icons-material";
import DoneOutlineIcon from "@mui/icons-material/DoneOutline";
import {
  useCreateFaQ,
  useFaQList,
  usePatchFaQ,
  useDeleteFaQ,
} from "../../api/noticeApi";
import { type Role } from "../../types/user";
import Dialog from "../../components/Dialog";
import "./faq.scss";

const FaQ = ({ role }: { role: Role }) => {
  const { data, isLoading } = useFaQList();
  const [openAddFaq, setOpenAddFaq] = useState(false);

  const { mutateAsync: createFaq } = useCreateFaQ();
  const { mutateAsync: patchFaq } = usePatchFaQ();
  const { mutateAsync: deleteFaq } = useDeleteFaQ();

  const [addQuestion, setAddQuestion] = useState("");
  const [addAnswer, setAddAnswer] = useState("");
  const [editedFaq, setEditedFaq] = useState<
    Record<
      number,
      {
        question: string;
        answer: string;
      }
    >
  >({});

  const handleAddFaq = async () => {
    if (!addQuestion) {
      alert("질문을 입력하세요.");
      return;
    }
    if (!addAnswer) {
      alert("답변을 입력하세요.");
      return;
    }

    await createFaq({ title: addQuestion, content: addAnswer });
    alert("성공적으로 추가되었습니다.");
    setAddAnswer("");
    setAddQuestion("");
    setOpenAddFaq(false);
  };

  const getIsEditing = useCallback(
    (id: number) => {
      return Object.keys(editedFaq).includes(id.toString());
    },
    [editedFaq]
  );

  const handleEditFinish = useCallback(
    (id: number) => {
      const { question, answer } = editedFaq[id];

      if (!question || !answer) {
        alert("질문과 답변을 입력하세요.");
        return;
      }
      patchFaq({ id, data: { title: question, content: answer } });
      delete editedFaq[id];
    },
    [editedFaq, patchFaq]
  );

  const openOnEdit = useCallback((id: string) => {
    const details = document.getElementById(
      `faq${id}`
    ) as HTMLDetailsElement | null;
    if (details) {
      details.open = true;
    }
  }, []);

  if (isLoading) return <p>로딩 중...</p>;

  return (
    <div className="wideContent">
      {role === "staff" && (
        <button onClick={() => setOpenAddFaq(true)} className="moveButton">
          추가하기
        </button>
      )}
      {data.faqs.length > 0 ? (
        <div>
          {data.faqs.map((faq: any, idx: number) =>
            getIsEditing(faq.id) ? (
              <div className="editingFaq">
                <div className="title">
                  Q{idx + 1}.{" "}
                  <input
                    type="text"
                    style={{ width: "100%", display: "inline-block" }}
                    value={editedFaq[faq.id].question}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    onChange={(e) => {
                      setEditedFaq((prev) => ({
                        ...prev,
                        [faq.id]: {
                          ...prev[faq.id],
                          question: e.target.value.slice(0, 50),
                        },
                      }));
                    }}
                  />
                  <DoneOutlineIcon
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditFinish(faq.id);
                    }}
                  />
                </div>
                <textarea
                  style={{
                    width: "100%",
                    minHeight: "200px",
                    boxSizing: "border-box",
                  }}
                  value={editedFaq[faq.id].answer}
                  onChange={(e) => {
                    setEditedFaq((prev) => ({
                      ...prev,
                      [faq.id]: {
                        ...prev[faq.id],
                        answer: e.target.value,
                      },
                    }));
                  }}
                />
              </div>
            ) : (
              <details id={`faq${faq.id}`}>
                <summary>
                  <div className="title">
                    Q{idx + 1}. {faq.title}
                    {role === "staff" && (
                      <>
                        <EditIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            openOnEdit(faq.id);
                            setEditedFaq((prev) => ({
                              ...prev,
                              [faq.id]: {
                                question: faq.title,
                                answer: faq.content,
                              },
                            }));
                          }}
                        />
                        <Delete
                          color="error"
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (window.confirm("정말 삭제하시겠습니까?")) {
                              await deleteFaq({ id: faq.id });
                              alert("삭제되었습니다.");
                            }
                          }}
                        />
                      </>
                    )}
                  </div>
                </summary>
                <AccordionDetails sx={{ whiteSpace: "pre-wrap" }}>
                  <p>A{idx + 1}.</p>
                  <p>{faq.content}</p>
                </AccordionDetails>
              </details>
            )
          )}
        </div>
      ) : (
        <p>등록된 FAQ가 없습니다.</p>
      )}
      {openAddFaq && (
        <Dialog
          buttons={["ok", "close"]}
          close={() => setOpenAddFaq(false)}
          ok={() => handleAddFaq()}
          title={"FAQ 추가"}
        >
          <div className="binary-grid wide">
            <>
              <p style={{ textAlign: "center" }}>질문: </p>
              <input
                type="text"
                value={addQuestion}
                onChange={(e) => {
                  setAddQuestion(e.target.value.slice(0, 50));
                }}
              />
            </>
            <>
              <p style={{ textAlign: "center" }}>답변: </p>
              <textarea
                style={{ minHeight: "200px" }}
                value={addAnswer}
                onChange={(e) => setAddAnswer(e.target.value)}
              />
            </>
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default FaQ;
